import { FC } from 'react';
import { ConditionalWrapper } from 'components/Shared/ConditionalWrapper';
import { Portal } from 'components/Shared/Portal';
import * as Styled from './styles';
import User from 'assets/images/avatars/user.png';
import NoData from 'assets/images/generic/no-data.png';
import PaymentFail from 'assets/images/payment/payment-fail.png';
import PaymentSuccess from 'assets/images/payment/payment-success.png';
import Payment from 'assets/images/payment/payment.png';
import SuccessSmall from 'assets/images/payment/success-small.gif';
import { TImageType, TSizeType } from 'interfaces';

export type TInfoBlockProps = {
  type: 'plain' | 'contrast';
  title: string;
  imageType?: TImageType;
  description?: string;
  price?: string;
  buttonProps?: {
    text: string;
    onClick: () => void;
    loading?: boolean;
    portalId?: string;
  };
  hintText?: string;
  alignToTop?: boolean;
  className?: string;
  size?: TSizeType;
};

const IMAGES: Record<NonNullable<TInfoBlockProps['imageType']>, string> = {
  payment: Payment,
  'payment-fail': PaymentFail,
  'payment-success': PaymentSuccess,
  empty: NoData,
  success: SuccessSmall,
  user: User
};

const SIZE: Record<TSizeType, number> = {
  small: 130,
  medium: 220,
  large: 320,
  huge: 480
};

export const InfoBlock: FC<TInfoBlockProps> = ({
  type = 'plain',
  title,
  imageType,
  description,
  price,
  buttonProps,
  hintText,
  alignToTop,
  className,
  size = 'medium'
}) => {
  return (
    <Styled.InfoBlock
      type={type}
      $alignToTop={alignToTop}
      className={className}
    >
      {imageType && (
        <Styled.Image
          src={IMAGES[imageType]}
          height={SIZE[size]}
          preview={false}
          alt={`${IMAGES[imageType]}`}
        />
      )}
      <Styled.Title>{title}</Styled.Title>
      {description && <Styled.Description>{description}</Styled.Description>}
      {price && <Styled.Price>{price}</Styled.Price>}
      {buttonProps && (
        <ConditionalWrapper
          condition={!!buttonProps.portalId}
          wrapper={children => (
            <Portal rootId={buttonProps.portalId}>{children}</Portal>
          )}
        >
          <Styled.Button
            onClick={buttonProps?.onClick}
            type="primary"
            loading={buttonProps.loading}
            $wide={!!buttonProps.portalId}
          >
            {buttonProps?.text}
          </Styled.Button>
        </ConditionalWrapper>
      )}
      {hintText && <Styled.HintText>{hintText}</Styled.HintText>}
    </Styled.InfoBlock>
  );
};
